import { z } from "zod/v4-mini";

import { type PumpDeviceBrand, pumpDeviceBrandSchema } from "@/models/MedicalDeviceModel.ts";
import { Profession } from "@/models/ProfessionModel.ts";
import { PaletteColors } from "@/theme/PaletteOverrides.ts";
import { Schema, cz } from "@/utils/zodUtils.ts";

import { ConditionType, MedicationSchema, conditionTypeSchema, medicationSchemeSchema } from "./ConditionModel";

export type NurseInclusionPatientStatus =
  | "suggested_to_doc"
  | "doctor_refused"
  | "patient_included"
  | "nurse_cancelled";

export const nurseInclusionPatientStatusSchema: Schema<NurseInclusionPatientStatus> = z.literal([
  "suggested_to_doc",
  "doctor_refused",
  "patient_included",
  "nurse_cancelled",
]);

export type NurseInclusionPreIncludedBy = {
  id: string;
  firstName: string;
  lastName: string;
  qualification: "nurse";
  profilePicture?: {
    url: string;
  };
};

export type TelemonitoringDoctor = {
  id: string;
  firstName: string;
  lastName: string;
};

export type NurseInclusionPatient = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  helperPhone?: string;
  status: NurseInclusionPatientStatus;
  inclusionReason: string;
  conditionType?: ConditionType;
  conditionMedicationSchema?: MedicationSchema;
  pumpModel?: PumpDeviceBrand;
  birthDate?: string;
  termDate?: string;
  preIncludedBy: NurseInclusionPreIncludedBy;
  doctor: TelemonitoringDoctor;
  createdAt: string;
};

const nurseInclusionPreIncludedBySchema: Schema<NurseInclusionPreIncludedBy> = cz.camelCaseObject({
  id: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  qualification: z.literal(["nurse"]),
  profile_picture: cz.optional(
    z.object({
      url: z.string(),
    }),
  ),
});

const telemonitoringDoctorSchema: Schema<TelemonitoringDoctor> = cz.camelCaseObject({
  id: z.string(),
  first_name: z.string(),
  last_name: z.string(),
});

export const nurseInclusionPatientModelSchema: Schema<NurseInclusionPatient> = cz.camelCaseObject({
  id: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string(),
  phone_number: z.string(),
  helper_phone: cz.optional(z.string()),
  status: nurseInclusionPatientStatusSchema,
  inclusion_reason: z.string(),
  condition_type: cz.optional(conditionTypeSchema),
  condition_medication_schema: cz.optional(medicationSchemeSchema),
  pump_model: cz.optional(pumpDeviceBrandSchema),
  birth_date: cz.optional(z.string()),
  term_date: cz.optional(z.string()),
  pre_included_by: nurseInclusionPreIncludedBySchema,
  doctor: telemonitoringDoctorSchema,
  created_at: cz.datetime(),
});

export type NursePreInclusionDirectives = {
  doctors: {
    id: string;
    firstName: string;
    lastName: string;
    qualification: Profession;
  }[];
};

export const nursePreInclusionDirectivesSchema: Schema<NursePreInclusionDirectives> = cz.camelCaseObject({
  doctors: cz.forcedArray(
    cz.camelCaseObject({
      id: z.string(),
      first_name: z.string(),
      last_name: z.string(),
      qualification: z.literal(["doctor"]),
    }),
  ),
});

// Request type for creating a nurse inclusion suggestion (nurse endpoint)
// Uses API values (snake_case and API enum values)
export type CreateNurseInclusionSuggestionRequest = {
  doctor_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  helper_phone?: string;
  inclusion_reason: string;
  condition_type?: ConditionType;
  condition_medication_schema?: MedicationSchema;
  pump_model?: PumpDeviceBrand;
  birth_date?: string;
  term_date?: string;
};

export const nurseInclusionStatusTlMapping = (status: NurseInclusionPatientStatus | undefined) => {
  switch (status) {
    case "suggested_to_doc":
      return "models.nurseInclusionPatientStatus.suggestedToDoc";
    case "doctor_refused":
      return "models.nurseInclusionPatientStatus.doctorRefused";
    case "patient_included":
      return "models.nurseInclusionPatientStatus.patientIncluded";
    case "nurse_cancelled":
      return "models.nurseInclusionPatientStatus.nurseCancelled";
    default:
      return "models.nurseInclusionPatientStatus.unknown";
  }
};

export const nurseInclusionStatusColorMapping = (status: NurseInclusionPatientStatus | undefined): PaletteColors => {
  switch (status) {
    case "suggested_to_doc":
      return "orange";
    case "doctor_refused":
      return "red";
    case "patient_included":
      return "success";
    case "nurse_cancelled":
      return "neutral";
    default:
      return "neutral";
  }
};
